import { getDateFnsLocale } from './locales';
import {
    //
    startOfWeek,
    startOfISOWeek,
    startOfQuarter,
    endOfQuarter,
    endOfWeek,
    endOfISOWeek,
    differenceInMonths,
    differenceInQuarters,
    differenceInYears,
    differenceInWeeks,
    subMilliseconds,
    startOfYear,
    setWeek,
    setYear,
    getQuarter as getQuarterDFNS,
    addDays,
    getWeek,
    getISOWeek,
    addQuarters,
    format,
    isValid,
    addMinutes,
    parseISO,
	addMonths,
	addWeeks,
	addSeconds,
	addYears,
} from 'date-fns';
import {
    //
    getTimezoneOffset,
} from 'date-fns-tz';
import { enUS } from 'date-fns/locale';

let locale = enUS;
const today = new Date();
const { timeZone } = new Intl.DateTimeFormat().resolvedOptions();

// The app only loads after this has run as a guarantee that we have the right locale
export async function setLocale(name: string) {
    const newLocale = await getDateFnsLocale(name);
    if (newLocale) {
        locale = newLocale;
    }
}

export function getLocale() {
	return locale;
}

export function firstDayNumberOfWeek(useISO8601: boolean): number {
	if (useISO8601) {
		return 1;
	}
    return startOfWeek(today, { locale }).getDay();
}


export function lastDayNumberOfWeek(useISO8601: boolean): number {
    const firstDayOfWeek = firstDayNumberOfWeek(useISO8601);
    if (firstDayOfWeek === 0) return 6;
    else return firstDayOfWeek - 1;
}

export function firstDayOfYear(): number {
    return startOfYear(today).getDay();
}

export function specifyUtc(date: Date): string {
    if (date == null) return null;
    const offset = date.getTimezoneOffset();
    const myDate = Date.parse(date.toString()) - offset * 60 * 1000;
    const dateAsISO = new Date(myDate).toISOString();
    return dateAsISO;
}

export function getDateOfISOWeek(week: number, year: number): Date {
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    return ISOweekStart;
}

export function getStartDateOfWeek(week: number, year: number): Date {
    return setYear(setWeek(startOfWeek(today), week, { locale }), year);
}

export const getQuarter = getQuarterDFNS;

export const getWeekNumber = (date: Date, localeString: string, useISOweek: boolean): { week: string; year: string } => {
    let week = useISOweek ? getISOWeek(date) : getWeek(date);

    let firstWeekDay = getFirstDayOfWeek(date, useISOweek);
    let endWeekDay = lastDayOfWeek(date, useISOweek);

    // can't be more wrong in regards to every standard in the whole world.. but so be it!
    if (localeString === 'en-US' && useISOweek) {
        // iso has mondays as first day of week
        const pushedDate = date.getDay() === 0 ? addDay(date) : date;
        week = getWeek(pushedDate);
        // push first and last as well
        firstWeekDay = addDay(firstWeekDay);
        endWeekDay = addDay(endWeekDay);
    } else if (localeString === 'en-US' && !useISOweek) {
        // handle shitty US crap - not good
        if (firstWeekDay.getFullYear() !== endWeekDay.getFullYear()) {
            return { week: String(week), year: `${endWeekDay.getFullYear()}` };
        }
    }
    // first week of year rule (ISO 8601)
    if (useISOweek && firstWeekDay.getFullYear() !== endWeekDay.getFullYear()) {
        const weekYear = endWeekDay.getDate() >= 4 ? endWeekDay.getFullYear() : firstWeekDay.getFullYear();

        return { week: String(week), year: weekYear + '' };
    }

    return { week: String(week), year: firstWeekDay.getFullYear() + '' };
};

export function addYear(date: Date, count: number = 1): Date {
	return addYears(date, count)
}

export function addQuarter(date: Date, count: number = 1): Date {
    return addQuarters(date, count);
}

export function addMonth(date: Date, count: number = 1): Date {
    return addMonths(date, count);
}

export function addWeek(date: Date, count: number = 1): Date {
	return addWeeks(date, count)
}

export function addDay(date: Date, count: number = 1): Date {
    return addDays(date, count);
}

export function addSecond(date: Date, count: number = 1): Date {
	return addSeconds(date, count)
}

export function getDaysCount(start: Date, end: Date): number {
    if (!start || !end) return -1;
    return Math.floor((+end - +start) / 86400000);
}

export function daysInMonth(month: number, year: number): number {
    return new Date(year, month, 0).getDate();
}

export function isWeekend(date: Date): boolean {
    if (date == null) return false;
    return date.getDay() === 6 || date.getDay() === 0;
}

export function equals(d1: Date, d2: Date): boolean {
    if (d1 == null || d2 == null) return false;
    return new Date(d1).getTime() === new Date(d2).getTime();
}

export function todaysDate(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

export function dateOnly(date: Date): Date {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export const firstWeekDay = firstDayNumberOfWeek;

export function lastWeekDay(useISO8601: boolean): number {
    const firstDay = firstDayNumberOfWeek(useISO8601);
    return firstDay === 0 ? 6 : firstDay - 1;
}

export function getFirstDayOfWeek(date?: Date, useISO8601?: boolean): Date {
    const firstDay = useISO8601 ? startOfISOWeek(date ?? new Date()) : startOfWeek(date ?? new Date(), { locale });
    return dateOnly(firstDay);
}

export function getFirstDayOfQuarter(date?: Date): Date {
    const firstDay = startOfQuarter(date ?? new Date());
    return dateOnly(firstDay);
}

export function lastDayOfQuarter(date?: Date): Date {
    const lastDay = endOfQuarter(date ?? new Date());
    return dateOnly(lastDay);
}

export function getFirstDayOfMonth(date?: Date): Date {
    const today = todaysDate();
    date = date || today;
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function getFirstDayOfYear(date?: Date): Date {
    const today = todaysDate();
    date = date || today;
    return new Date(date.getFullYear(), 0, 1);
}

export function firstDayOfNextWeek(useISO8601: boolean, date?: Date): Date {
    const firstDayOfWeek = firstDayNumberOfWeek(useISO8601);

    const today = todaysDate();
    date = date ? dateOnly(date) : today;
    const day = date.getDay(),
        diff = date.getDate() - day + (day === 0 ? -6 : firstDayOfWeek) + 7;
    return new Date(date.setDate(diff));
}

export function lastDayOfWeek(date?: Date, useISO8601?: boolean): Date {
    const lastDay = useISO8601 ? endOfISOWeek(date ?? new Date()) : endOfWeek(date ?? new Date());
    return dateOnly(lastDay);
}

export function firstDayOfNextMonth(date?: Date): Date {
    const today = todaysDate();
    date = date ? dateOnly(date) : today;
    date.setMonth(date.getMonth() + 1);
    date.setDate(1);
    return date;
}

export function lastDayOfMonth(date?: Date): Date {
    const today = todaysDate();
    date = date ? dateOnly(date) : today;
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function firstDayOfNextYear(date?: Date): Date {
    const today = todaysDate();
    date = date ? dateOnly(date) : today;
    date.setFullYear(date.getFullYear() + 1);
    date.setMonth(0);
    date.setDate(1);
    return date;
}

export function lastDayOfYear(date?: Date): Date {
    const today = todaysDate();
    date = date ? dateOnly(date) : today;
    return new Date(date.getFullYear(), 11, 31);
}

const _MS_PER_DAY = 1000 * 60 * 60 * 24;
export function getDifferenceInDays(d1: Date, d2: Date): number {
    // Discard the time and time-zone information.
    const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
    const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());

    return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}

export const getDifferenceInMonths = differenceInMonths;

export const getDifferenceInQuarters = differenceInQuarters;

export const getDifferenceInYears = differenceInYears;

export const getDifferenceInWeeks = differenceInWeeks;

export function formatDate(date: Date): string {
    return format(date, 'EEE do MMM yyyy', { locale });
}

export function formatDateWithTimeSimple(date: Date): string {
    const today = todaysDate();
    const dateDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let dateString: string;
    if (today.getTime() === dateDate.getTime()) dateString = 'Today';
    else dateString = formatDate(date);

    const hours = `0${date.getHours()}`.slice(-2);
    const minutes = `0${date.getMinutes()}`.slice(-2);
    const seconds = `0${date.getSeconds()}`.slice(-2);
    const timeString = `${hours}:${minutes}:${seconds}`;
    const fullString = `${dateString} - ${timeString}`;
    return fullString;
}

const getUTCOffset = (fromDate = new Date()) => Math.abs(fromDate.getTimezoneOffset());

export function asUserLocalWithTime(utcDate: Date): string {
    if (utcDate == null) {
        return '';
    }
    const localDate = addMinutes(utcDate, getUTCOffset());
    return format(localDate, 'yyyy-MM-dd HH:mm:ss xx', { locale });
}

export function asUserLocalWithTimeNoZ(utcDate: Date): string {
    if (utcDate == null) {
        return '';
    }
    const localDate = addMinutes(utcDate, getUTCOffset());
    return format(localDate, 'yyyy-MM-dd HH:mm:ss', { locale });
}

export function asUserLocalWithTimePretty(utcDate: Date): string {
    if (utcDate == null) {
        return '';
    }
    const localDate = addMinutes(utcDate, getUTCOffset());
    return format(localDate, 'eee do MMM yyyy HH:mm', { locale });
}

export function asUserLocal(utcDate: Date): string {
    if (utcDate == null) return '';
    const localDate = addMinutes(utcDate, getUTCOffset());
    const formattedLocalDate = format(localDate, 'EEE do MMM yyyy');
    if (!utcDate || formattedLocalDate === 'Mon 1st Jan 0001') return ''; //TODO EWI, THIS WAS CHANGED TO AVOID 'Invalid Date'
    return formattedLocalDate;
}

export function tryParse(obj: any): Date {
    if (obj == null) return null;
    const date = new Date(obj);
    if (!date.valueOf()) return null;
    return date;
}

export function toUtcString(date: Date): string {
    return format(
        new Date(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds(),
            date.getUTCMilliseconds(),
        ),
        "yyyy-MM-dd'T'HH:mm:ss'Z'",
    );
}

export function fixJsonAutoParse(date: Date): Date {
    const newDate = subMilliseconds(date, getTimezoneOffset(timeZone));
    newDate.setMilliseconds(0);
    return newDate;
}

export function isNullOrMin(date: Date): boolean {
    return date == null || (date as any) === '0001-01-01T00:00:00' || new Date(date).getFullYear() <= 1;
}

export function isValidISODate(value: string | Date): boolean {
    return isValid(parseISO(String(value)).getTime());
}
